import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import type { FeedPage } from './base-types';
import { qs } from './base';
import { api } from './client';
import type {
  Chronometry,
  ClientDealFull,
  ParticipationRow,
  UnlinkedPerson,
  CrmSettings,
  DealBoard,
  DealCard,
  DealList,
  DealOption,
  LeadBoard,
  LeadCard,
  LeadList,
  LeadSource,
  MoveOptions,
  ReasonKind,
  RefusalReason,
  RegistrationCard,
  RegistrationOptions,
  RegList,
  RepeatReport,
  RosterMember,
} from './m3-types';

/**
 * Серверное состояние M3. Ключи — под ['crm', …] и ['reg', …]. Любая
 * правка перечитывает оба префикса и базу M2: операция с номером меняет
 * стадию сделки, сделка — карточку клиента, и наоборот (ТЗ M3 §6).
 */

type Params = Record<string, string | number | null | undefined>;

// ─── настройки и справочники ───

export const useCrmSettings = () =>
  useQuery({ queryKey: ['crm', 'settings'], queryFn: () => api.get<CrmSettings>('/crm/settings') });

export const useLeadSources = () =>
  useQuery({ queryKey: ['crm', 'sources'], queryFn: () => api.get<LeadSource[]>('/lead-sources'), staleTime: 60_000 });

export const useReasons = (appliesTo?: ReasonKind) =>
  useQuery({
    queryKey: ['crm', 'reasons', appliesTo ?? 'all'],
    queryFn: () => api.get<RefusalReason[]>(`/refusal-reasons${qs({ appliesTo })}`),
    staleTime: 60_000,
  });

// ─── заявки ───

export const useLeadList = (params: Params) =>
  useQuery({
    queryKey: ['crm', 'leads', params],
    queryFn: () => api.get<LeadList>(`/leads${qs(params)}`),
    placeholderData: keepPreviousData,
  });

export const useLeadBoard = (params: Params, enabled: boolean) =>
  useQuery({
    queryKey: ['crm', 'lead-board', params],
    queryFn: () => api.get<LeadBoard>(`/leads/board${qs(params)}`),
    placeholderData: keepPreviousData,
    enabled,
  });

export const useLead = (id: string | undefined) =>
  useQuery({ queryKey: ['crm', 'lead', id], queryFn: () => api.get<LeadCard>(`/leads/${id}`), enabled: Boolean(id) });

// ─── сделки ───

export const useDealList = (params: Params) =>
  useQuery({
    queryKey: ['crm', 'deals', params],
    queryFn: () => api.get<DealList>(`/deals${qs(params)}`),
    placeholderData: keepPreviousData,
  });

export const useDealBoard = (params: Params, enabled: boolean) =>
  useQuery({
    queryKey: ['crm', 'deal-board', params],
    queryFn: () => api.get<DealBoard>(`/deals/board${qs(params)}`),
    placeholderData: keepPreviousData,
    enabled,
  });

export const useDeal = (id: string | undefined) =>
  useQuery({ queryKey: ['crm', 'deal', id], queryFn: () => api.get<DealCard>(`/deals/${id}`), enabled: Boolean(id) });

export const useClientDeals = (clientId: string | undefined, enabled = true) =>
  useQuery({
    queryKey: ['crm', 'client-deals', clientId],
    queryFn: () => api.get<ClientDealFull[]>(`/clients/${clientId}/deals`),
    enabled: Boolean(clientId) && enabled,
  });

// ─── участие в карточках M2 ───

type Participation = { current: ParticipationRow[]; history: ParticipationRow[] };

export const useTeamParticipation = (teamId: string, enabled: boolean) =>
  useQuery({
    queryKey: ['reg', 'team', teamId],
    queryFn: () => api.get<Participation>(`/teams/${teamId}/registrations`),
    enabled,
  });

export const useClientParticipation = (clientId: string, enabled: boolean) =>
  useQuery({
    queryKey: ['reg', 'client', clientId],
    queryFn: () => api.get<Participation>(`/clients/${clientId}/participation`),
    enabled,
  });

export const useStudioParticipation = (studioId: string, enabled: boolean) =>
  useQuery({
    queryKey: ['reg', 'studio', studioId],
    queryFn: () => api.get<Record<string, ParticipationRow | null>>(`/studios/${studioId}/registrations`),
    enabled,
  });

export const useTeamUnlinked = (teamId: string, enabled: boolean) =>
  useQuery({
    queryKey: ['reg', 'unlinked', teamId],
    queryFn: () => api.get<UnlinkedPerson[]>(`/teams/${teamId}/unlinked-rows`),
    enabled,
  });

// ─── регистрации ───

export const useRegList = (params: Params) =>
  useQuery({
    queryKey: ['reg', 'list', params],
    queryFn: () => api.get<RegList>(`/registrations${qs(params)}`),
    placeholderData: keepPreviousData,
  });

export const useRegistration = (id: string | undefined) =>
  useQuery({
    queryKey: ['reg', 'card', id],
    queryFn: () => api.get<RegistrationCard>(`/registrations/${id}`),
    enabled: Boolean(id),
  });

export const useRegOptions = (dealId: string | null) =>
  useQuery({
    queryKey: ['reg', 'options', dealId],
    queryFn: () => api.get<RegistrationOptions>(`/registrations/options${qs({ dealId })}`),
    enabled: Boolean(dealId),
  });

export const useDealOptions = () =>
  useQuery({ queryKey: ['reg', 'deal-options'], queryFn: () => api.get<DealOption[]>('/registrations/deals') });

export const useRoster = (id: string, enabled: boolean) =>
  useQuery({
    queryKey: ['reg', 'roster', id],
    queryFn: () => api.get<RosterMember[]>(`/registrations/${id}/team-roster`),
    enabled,
  });

export const useMoveOptions = (id: string) =>
  useQuery({ queryKey: ['reg', 'move-options', id], queryFn: () => api.get<MoveOptions>(`/registrations/${id}/move-options`) });

export const useChronometry = (eventId: string | null) =>
  useQuery({
    queryKey: ['reg', 'chrono', eventId],
    queryFn: () => api.get<Chronometry>(`/events/${eventId}/chronometry`),
    enabled: Boolean(eventId),
  });

export const useRepeat = (eventId: string | null) =>
  useQuery({
    queryKey: ['reg', 'repeat', eventId],
    queryFn: () => api.get<RepeatReport>(`/events/${eventId}/repeat-participants`),
    enabled: Boolean(eventId),
  });

// ─── лента заявки, сделки и номера ───

export type M3FeedScope = 'leads' | 'deals' | 'registrations';

export const useM3Feed = (scope: M3FeedScope, id: string) =>
  useInfiniteQuery({
    queryKey: [scope === 'registrations' ? 'reg' : 'crm', 'feed', scope, id],
    queryFn: ({ pageParam }) => api.get<FeedPage>(`/${scope}/${id}/feed${qs({ before: pageParam })}`),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next ?? undefined,
  });

// ─── правка ───

/** Любая правка M3 перечитывает CRM, регистрации и базу M2 */
export function useM3Mutation<TArgs, TResult>(
  fn: (args: TArgs) => Promise<TResult>,
  options?: Omit<UseMutationOptions<TResult, Error, TArgs>, 'mutationFn'>,
) {
  const client = useQueryClient();
  return useMutation<TResult, Error, TArgs>({
    mutationFn: fn,
    ...options,
    onSuccess: async (data, vars, ctx, mutation) => {
      await Promise.all([
        client.invalidateQueries({ queryKey: ['crm'] }),
        client.invalidateQueries({ queryKey: ['reg'] }),
        client.invalidateQueries({ queryKey: ['base'] }),
      ]);
      await options?.onSuccess?.(data, vars, ctx, mutation);
    },
  });
}
